// Admin data-model contracts: department reference and HR attribute mapping (PB-18-07, PD-540 –
// PD-546) and site-scoped retention overrides (PB-12-05, PD-547 – PD-552). Messages are i18n keys.
import { z } from 'zod';
import type { HostSyncRunResponse } from './people.ts';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const Reason = z.string().trim().min(3, 'v.reasonShort').max(500, 'v.tooLong');
const Version = z.number().int().min(1);
const AttrName = z.string().trim().min(1).max(100);
const ClassCode = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'v.invalid');
const TypeKey = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');
const Days = z.number().int('v.invalid').min(0, 'v.range').max(36_500, 'v.range');

// ------------------------------------------------------------------ departments (PB-18-07)
export type DepartmentSourceValue = 'manual' | 'migrated' | 'directory' | 'import' | 'hr';
export interface DepartmentDto {
  readonly id: string;
  readonly name: string;
  /** null = organisation-wide. */
  readonly siteId: string | null;
  readonly source: DepartmentSourceValue;
  readonly status: 'active' | 'inactive';
  /** Owning HR connector and the department's id there (read-only facts); null for local ones. */
  readonly hr: { readonly connectorId: string; readonly externalId: string } | null;
  /** Hosts linked to the reference (host managers only). */
  readonly hostCount: number | null;
  readonly deactivatedAt: string | null;
  readonly version: number;
}
export const DepartmentListQuery = z.object({ includeInactive: z.enum(['true', 'false']).optional(), siteId: Uuid.optional() });
export const CreateDepartmentRequest = z.object({ name: trimmed(120), siteId: Uuid.nullable().optional(), reason: Reason });
export const RenameDepartmentRequest = z.object({ expectedVersion: Version, name: trimmed(120), reason: Reason });
export const DepartmentStatusRequest = z.object({ expectedVersion: Version, reason: Reason });
export interface DepartmentChangeDto {
  readonly id: string;
  readonly kind: 'created' | 'renamed' | 'deactivated' | 'reactivated' | 'linked';
  readonly oldName: string | null;
  readonly newName: string | null;
  readonly source: DepartmentSourceValue;
  readonly actor: string | null;
  readonly runId: string | null;
  readonly reason: string | null;
  readonly hostsAffected: number;
  readonly at: string;
}

// ------------------------------------------------------------------ HR attribute mapping (PB-18-07)
export const HR_MAPPING_FIELDS = ['department', 'jobTitle', 'employeeNumber', 'officeSiteCode'] as const;
export const HrSyncSettingRequest = z.object({
  expectedVersion: z.number().int().min(0),
  enabled: z.boolean(),
  // Only mapped (approved) HR attributes are read; `department` names the attribute holding the HR department id.
  mapping: z.object({ department: AttrName.nullable().optional(), jobTitle: AttrName.nullable().optional(), employeeNumber: AttrName.nullable().optional(), officeSiteCode: AttrName.nullable().optional() }).strict(),
  maxDeactivationPercent: z.number().int().min(0, 'v.range').max(100, 'v.range').optional(),
  reason: Reason,
});
export const RunHrSyncRequest = z.object({ confirmDeactivations: z.boolean().default(false) });
export const LinkHrDepartmentRequest = z.object({ expectedVersion: Version, departmentId: Uuid, externalId: z.string().trim().min(1).max(200), reason: Reason });
export const LinkHostHrRequest = z.object({ expectedVersion: Version, connectorId: Uuid, externalId: z.string().trim().min(1).max(200), reason: Reason });
export interface HrConnectorDto {
  readonly connectorId: string;
  readonly name: string;
  readonly adapterCode: string;
  readonly siteId: string | null;
  readonly connectorStatus: 'draft' | 'enabled' | 'disabled';
  readonly simulator: boolean;
  /** Why a provider adapter cannot run yet (e.g. HR platform not chosen, H-05). */
  readonly blocker: string | null;
  readonly setting: { readonly enabled: boolean; readonly mapping: Readonly<Record<string, string | null>>; readonly maxDeactivationPercent: number; readonly version: number } | null;
  readonly ownedHosts: number;
  readonly inactiveHosts: number;
  readonly departments: { readonly active: number; readonly inactive: number };
  readonly lastRun: HostSyncRunResponse | null;
}

// ------------------------------------------------------------------ site retention overrides (PB-12-05)
export type RetentionSourceValue = 'site_visitor_type' | 'organisation_visitor_type' | 'site' | 'organisation';
export const SaveSiteRetentionDraftRequest = z.object({
  expectedVersion: z.number().int().min(0),
  // null removes the class from the draft (the shared rule applies again).
  rules: z.record(ClassCode, z.object({ durationDays: Days, note: optionalText(300) }).nullable()),
  visitorTypes: z.record(TypeKey, z.record(ClassCode, Days.nullable())).optional(),
  note: optionalText(500),
});
export const SiteRetentionCommandRequest = z.object({ expectedVersion: Version });
/** Removal needs the override version and the organisation policy number shown in the preview. */
export const RemoveSiteRetentionRequest = z.object({ expectedVersion: Version, policyNumber: z.number().int().min(1), reason: Reason });

export interface SiteRetentionVersionView {
  readonly id: string;
  readonly siteId: string;
  readonly number: number;
  readonly status: 'draft' | 'published' | 'superseded' | 'discarded' | 'removed';
  readonly rules: Readonly<Record<string, { readonly durationDays: number; readonly note: string | null }>>;
  readonly visitorTypes: Readonly<Record<string, Readonly<Record<string, number>>>>;
  readonly note: string | null;
  readonly basedOnPolicyNumber: number | null;
  readonly publishedAt: string | null;
  readonly removedAt: string | null;
  readonly removalReason: string | null;
  readonly version: number;
  readonly problems: ReadonlyArray<{ readonly path: string; readonly code: string }>;
}
export interface SiteRetentionLine {
  readonly classCode: string;
  readonly visitorType: string | null;
  readonly overrideDays: number;
  readonly shared: { readonly days: number | null; readonly source: RetentionSourceValue };
}
export interface SiteRetentionEffectiveLine {
  readonly classCode: string;
  readonly visitorType: string | null;
  readonly days: number | null;
  readonly source: RetentionSourceValue;
}
export interface SiteRetentionDetail {
  readonly siteId: string;
  /** Classes a site may override, and those that may also differ per visitor type. */
  readonly siteClasses: readonly string[];
  readonly perTypeClasses: readonly string[];
  readonly policy: { readonly id: string; readonly number: number } | null;
  readonly published: SiteRetentionVersionView | null;
  readonly draft: SiteRetentionVersionView | null;
  readonly history: readonly SiteRetentionVersionView[];
  /** The published override next to the shared rule it replaces (diff). */
  readonly lines: readonly SiteRetentionLine[];
  /** What applies at this site now, per class (and per visitor type with a typed duration). */
  readonly effective: readonly SiteRetentionEffectiveLine[];
  readonly canManage: boolean;
}
export interface SiteRetentionSummary {
  readonly items: ReadonlyArray<{ readonly siteId: string; readonly published: { readonly number: number; readonly publishedAt: string | null; readonly lines: number } | null; readonly draft: { readonly number: number; readonly version: number } | null }>;
  readonly canManage: boolean;
}
export interface SiteRetentionRemovalPreview {
  readonly siteId: string;
  readonly override: { readonly id: string; readonly number: number; readonly version: number };
  readonly policy: { readonly id: string; readonly number: number };
  /** Each override line and the shared rule that becomes effective (PB-12-05 edge test). */
  readonly lines: readonly SiteRetentionLine[];
  /** Records at this site whose due state changes, now, if the shared rules applied. */
  readonly records: ReadonlyArray<{
    readonly classCode: string;
    readonly becomesDue: number;
    readonly noLongerDue: number;
    readonly sample: ReadonlyArray<{ readonly resourceType: string; readonly resourceId: string; readonly change: 'becomes_due' | 'no_longer_due' }>;
    /** The planning limit was reached; counts are lower bounds. */
    readonly truncated: boolean;
  }>;
}
